import { useState } from 'react';
import { Loader2, Eye, X, Download } from 'lucide-react';
import { useReports } from '../../Hooks/useReports';
import api from '../../lib/api';
import { toast } from 'react-hot-toast';
import { canAccess } from '../../utils/permissions';
import ServerReportModal from './details/ServerReportModal';

export default function Reports() {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedReport, setSelectedReport] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const perPage = 10;
    const { reports, pagination, isLoading, error, approveReport, isApproving } = useReports(currentPage, perPage);

    const closeModal = () => setSelectedReport(null);

    const handleDownloadPDF = async () => {
        if (!selectedReport) return;

        setIsDownloading(true);
        try {
            const res = await api.get(`/user/reports/main/${selectedReport.report_id}/pdf`);

            const { download_url, filename } = res.data.data;

            if (!download_url) {
                toast.error('No download link received from server.');
                return;
            }

            const link = document.createElement('a');
            link.href = download_url;
            link.setAttribute('download', filename || `report_${selectedReport.report_id}.pdf`);
            link.setAttribute('target', '_blank');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success('Report PDF download started!');
        } catch (error) {
            console.error('PDF download failed:', error);
            toast.error(
                error.response?.data?.message ||
                'Failed to download report PDF. Please try again.'
            );
        } finally {
            setIsDownloading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-10 h-10 animate-spin text-teal-600" />
            </div>
        );
    }

    if (error) {
        return (
            <section className="min-h-screen bg-gray-50 px-4 py-8">
                <div className="text-center py-20 text-red-600 font-bold">
                    Error loading reports: {error.message}
                </div>
            </section>
        );
    }

    if (!canAccess("view_reports")) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center text-sm font-medium">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <Eye className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
                <p className="text-gray-500 max-w-md font-medium">
                    You don't have the required permissions to view reports.
                </p>
            </div>
        );
    }

    return (
        <section className="min-h-screen">
            <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
                <div className="px-4 py-4">
                    <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
                    <p className="text-sm text-gray-500">View all completed survey reports</p>
                </div>
            </div>

            <div className="px-4 py-6">
                {reports.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center">
                        <p className="text-gray-500 text-lg">No reports available yet.</p>
                        <p className="text-sm text-gray-400 mt-2">Completed jobs will generate reports here.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {reports.map((report) => {
                            const submittedDate = report.submitted_at
                                ? new Date(report.submitted_at).toLocaleDateString(undefined, {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                })
                                : 'Date N/A';
                            const submittedTime = report.submitted_at
                                ? new Date(report.submitted_at).toLocaleTimeString(undefined, {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true,
                                })
                                : '';

                            return (
                                <div
                                    key={report.report_id}
                                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition"
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div>
                                            <h3 className="font-bold text-gray-900">
                                                {report.job?.job_number || `Report #${report.report_id}`}
                                            </h3>
                                            <p className="text-sm text-gray-500 mt-1">
                                                Submitted {submittedDate} {submittedTime && `at ${submittedTime}`}
                                            </p>
                                        </div>
                                        <span className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${report.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                                            report.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                'bg-yellow-100 text-yellow-800'
                                            }`}>
                                            {report.status || 'Pending'}
                                        </span>
                                    </div>

                                    <div className="space-y-2 text-sm text-gray-600">
                                        <p>
                                            <span className="font-medium">Surveyor:</span>{' '}
                                            {report.surveyor?.name || 'N/A'}
                                        </p>
                                        <p>
                                            <span className="font-medium">Client:</span>{' '}
                                            {report.quote?.client_name || 'N/A'}
                                        </p>
                                        <p>
                                            <span className="font-medium">Boat:</span>{' '}
                                            {report.quote?.boat_type} {report.quote?.boat_make} ({report.quote?.boat_year})
                                        </p>
                                        <p>
                                            <span className="font-medium">Categories Covered:</span>{' '}
                                            {report.category_reports_count} ({report.categories_covered.join(', ')})
                                        </p>
                                    </div>

                                    <div className="mt-6 flex gap-3">
                                        <button
                                            onClick={() => setSelectedReport(report)}
                                            className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition flex items-center justify-center gap-2"
                                        >
                                            <Eye className="w-4 h-4" />
                                            View Details
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {pagination.last_page > 1 && (
                    <div className="mt-10 flex items-center justify-center gap-2">
                        <button
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                        >
                            Previous
                        </button>
                        {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-4 py-2 rounded-lg transition ${page === currentPage
                                    ? 'bg-emerald-600 text-white'
                                    : 'bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pagination.last_page))}
                            disabled={currentPage === pagination.last_page}
                            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {selectedReport && (
                <ServerReportModal
                    report={selectedReport}
                    onClose={closeModal}
                    onDownload={handleDownloadPDF}
                    isDownloading={isDownloading}
                    approveReport={approveReport}
                    isApproving={isApproving}
                    canAccess={canAccess}
                />
            )}
        </section>
    );
}